import { useEffect } from 'react';
import { useCartStore } from '@/stores/cartStore';

/**
 * Sync Shopify cart on mount + tab visibility change.
 * Initial sync is deferred to requestIdleCallback (or 1500ms timeout) so it never
 * competes with LCP/hydration on the critical path. The cart UI is in a drawer
 * that the user must click to open, so a small delay is invisible to UX.
 */
export function useCartSync() {
  const syncCart = useCartStore(state => state.syncCart);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let idleId: number | null = null;
    let timeoutId: number | null = null;

    const runInitialSync = () => { syncCart(); };
    const w = window as any;

    if ('requestIdleCallback' in window) {
      idleId = w.requestIdleCallback(runInitialSync, { timeout: 2500 });
    } else {
      timeoutId = w.setTimeout(runInitialSync, 1500);
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') syncCart();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (idleId !== null && 'cancelIdleCallback' in window) w.cancelIdleCallback(idleId);
      if (timeoutId !== null) clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [syncCart]);
}
